// ─────────────────────────────────────────────────────────────────────
// ACTIVITY CAPTURE — the sibling of consoleCapture: two small ring buffers the
// bug report attaches, because «what did you do just before?» is the question
// every report starts with and the reporter rarely remembers the answer.
//
//   • ACTIONS — the user's most recent clicks, field changes and navigations,
//     as short labels («klikk: Lagre», «felt: Tittel», «side: #mobile»).
//   • FAILED REQUESTS — fetch calls that came back ≥ 400 or never came back.
//
// PRIVACY, deliberately narrow: an action records WHAT was clicked or which
// field changed — its visible label — and never what was typed. A request
// records method, path (no query string), status and duration — never a body
// or a header. Labels are clipped, so a long row of text cannot ride along.
// A label's text is read WITHOUT the form controls inside it: a React-controlled
// <textarea> carries its typed value as text content (React keeps
// `defaultValue` equal to `value`, and `defaultValue` IS the text content), so
// a wrapping label's raw `textContent` ends with what was typed (#860).
// A failure that repeats — a 2-second poll during an outage — is ONE entry with
// a repeat count, so it cannot push the failure the user actually hit out of
// the buffer (#860).
// Anything inside `[data-bug-reporter]` is ignored: opening the reporter and
// typing into it is not the user's trail to the bug.
//
// `setupActivityCapture()` is idempotent and installed once at app start next
// to `setupConsoleCapture()`. The fetch wrapper calls the original and returns
// its result untouched; it only observes.
// ─────────────────────────────────────────────────────────────────────

export const ACTION_CAPACITY = 15;
export const FAILED_REQUEST_CAPACITY = 15;
const LABEL_MAX = 60;

export interface CapturedAction {
  time: number;
  kind: 'click' | 'input' | 'nav';
  label: string;
}

export interface FailedRequest {
  time: number;
  method: string;
  path: string;
  /** HTTP status, or 0 when no response arrived (network error / abort). */
  status: number;
  ms: number;
  /** How many times this method + path + status failed while it was buffered; absent means once. */
  repeats?: number;
}

export const __actionLog__: CapturedAction[] = [];
export const __failedRequests__: FailedRequest[] = [];
let installed = false;

function pushBounded<T>(list: T[], entry: T, capacity: number) {
  list.push(entry);
  while (list.length > capacity) list.shift();
}

export function recordAction(kind: CapturedAction['kind'], label: string) {
  const clean = clip(label);
  if (!clean) return;
  pushBounded(__actionLog__, { time: Date.now(), kind, label: clean }, ACTION_CAPACITY);
}

export function recordFailedRequest(entry: Omit<FailedRequest, 'time' | 'repeats'>) {
  // The same call failing the same way again is one entry, moved to the newest
  // position with its count — not a new slot. Otherwise a poller fills the
  // buffer with its own failures and evicts the one the report is about.
  const at = __failedRequests__.findIndex(
    (r) => r.method === entry.method && r.path === entry.path && r.status === entry.status,
  );
  const repeats = at === -1 ? 1 : (__failedRequests__[at].repeats ?? 1) + 1;
  if (at !== -1) __failedRequests__.splice(at, 1);
  pushBounded(
    __failedRequests__,
    { ...entry, time: Date.now(), ...(repeats > 1 ? { repeats } : {}) },
    FAILED_REQUEST_CAPACITY,
  );
}

function clip(s: string | null | undefined): string {
  const one = String(s ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return one.length > LABEL_MAX ? `${one.slice(0, LABEL_MAX - 1)}…` : one;
}

const FORM_CONTROLS = 'input, textarea, select';

/**
 * An element's text without the form controls inside it. A <textarea>'s text
 * content is its (React-controlled) value and a <select>'s is every option, so
 * a label wrapping either must not be read whole.
 */
function textOutsideControls(el: Element): string {
  if (!el.querySelector(FORM_CONTROLS)) return el.textContent ?? '';
  const copy = el.cloneNode(true) as Element;
  copy.querySelectorAll(FORM_CONTROLS).forEach((c) => c.remove());
  return copy.textContent ?? '';
}

const CLICKABLE =
  'button, a, [role="button"], [role="tab"], [role="menuitem"], [role="option"], ' +
  'summary, label, select, input[type="checkbox"], input[type="radio"]';

/** The visible name of a clicked control — never a typed value. */
export function describeElement(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const title = el.getAttribute('title');
  // A clicked <label> may wrap the field itself — clicking into a wrapped
  // textarea lands here — so its controls are dropped before it is read.
  const text = el.querySelector(FORM_CONTROLS)
    ? textOutsideControls(el)
    : ((el as HTMLElement).innerText ?? el.textContent ?? '');
  if (clip(text)) return text;
  if (title) return title;
  const name = el.getAttribute('name') || el.getAttribute('placeholder');
  return name || el.tagName.toLowerCase();
}

/** The name of a field: its label, aria-label, placeholder or name — not its value. */
export function describeField(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const id = el.getAttribute('id');
  if (id && typeof document !== 'undefined') {
    const safe = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
    const lbl = document.querySelector(`label[for="${safe}"]`);
    const text = lbl ? textOutsideControls(lbl) : '';
    if (clip(text)) return text;
  }
  // Every label path reads the label WITHOUT its controls: the field may sit
  // inside the very label that names it (#860).
  const wrapping = el.closest('label');
  const wrapped = wrapping ? textOutsideControls(wrapping) : '';
  if (clip(wrapped)) return wrapped;
  // A visual label just above the field — the app's common pattern is
  // <div><label>Tittel</label><input/></div>, without `for`.
  const sibling = el.parentElement?.querySelector(':scope > label');
  const beside = sibling ? textOutsideControls(sibling) : '';
  if (clip(beside)) return beside;
  return el.getAttribute('placeholder') || el.getAttribute('name') || el.tagName.toLowerCase();
}

function insideReporter(el: Element | null): boolean {
  return !!el?.closest?.('[data-bug-reporter]');
}

function pathOf(input: RequestInfo | URL): string {
  try {
    const raw =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const u = new URL(raw, typeof location !== 'undefined' ? location.href : 'http://localhost');
    return u.pathname; // no query string, no host
  } catch {
    return '?';
  }
}

export function setupActivityCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null;
      if (!target?.closest || insideReporter(target)) return;
      const el = target.closest(CLICKABLE);
      if (!el) return;
      recordAction('click', describeElement(el));
    },
    true,
  );

  document.addEventListener(
    'change',
    (e) => {
      const el = e.target as Element | null;
      if (!el?.closest || insideReporter(el)) return;
      if (!/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      const type = (el.getAttribute('type') || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') return; // the click already said it
      recordAction('input', describeField(el));
    },
    true,
  );

  const nav = () => recordAction('nav', location.hash || location.pathname || '/');
  window.addEventListener('hashchange', nav);
  window.addEventListener('popstate', nav);

  if (typeof window.fetch === 'function') {
    const original = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const started = Date.now();
      const method = (
        init?.method ||
        (typeof input === 'object' && 'method' in input ? input.method : '') ||
        'GET'
      ).toUpperCase();
      try {
        const res = await original(input, init);
        if (res.status >= 400) {
          recordFailedRequest({
            method,
            path: pathOf(input),
            status: res.status,
            ms: Date.now() - started,
          });
        }
        return res;
      } catch (err) {
        recordFailedRequest({ method, path: pathOf(input), status: 0, ms: Date.now() - started });
        throw err;
      }
    };
  }
}

function hhmmss(t: number): string {
  return new Date(t).toLocaleTimeString('no-NO');
}

const KIND_LABEL: Record<CapturedAction['kind'], string> = {
  click: 'klikk',
  input: 'felt',
  nav: 'side',
};

/** Snapshot copies — stored on the report, so later activity cannot change it. */
export function getCapturedActions(): CapturedAction[] {
  return __actionLog__.map((a) => ({ ...a }));
}

export function getFailedRequests(): FailedRequest[] {
  return __failedRequests__.map((r) => ({ ...r }));
}

export function formatActions(actions: CapturedAction[] | null | undefined): string {
  if (!actions?.length) return '';
  return actions
    .map((a) => `[${hhmmss(a.time)}] ${KIND_LABEL[a.kind] ?? a.kind}: ${a.label}`)
    .join('\n');
}

export function formatFailedRequests(reqs: FailedRequest[] | null | undefined): string {
  if (!reqs?.length) return '';
  return reqs
    .map(
      (r) =>
        `[${hhmmss(r.time)}] ${r.method} ${r.path} → ${r.status === 0 ? 'ingen svar' : r.status} (${r.ms} ms)${(r.repeats ?? 1) > 1 ? ` ×${r.repeats}` : ''}`,
    )
    .join('\n');
}
